import React, { createContext, useContext } from 'react';

/* ── PAGER-1 — EL CONTRATO MONTADA / ACTIVA ───────────────────────────────────
 *
 * HOY "montarse" significa TRES cosas a la vez, y el router las tiene pegadas:
 * existir en el DOM, pedir datos, y anunciar "el usuario me está viendo". Eso
 * funciona mientras una sola raíz esté viva — que es exactamente lo que el
 * pager (PAGER-2/3) va a romper al pre-montar las cuatro raíces DORMIDAS. Este
 * módulo separa el tercer significado de los otros dos ANTES de que exista el
 * pager, para que cada efecto declare de qué momento depende.
 *
 * LA DOCTRINA DEL CONTRATO
 *
 *   DORMIDO PUEDE LEER LOCAL Y PINTAR. Lecturas de SQLite / bodega, presencia
 *   del teléfono, semilla que ya está en memoria: una raíz dormida puede
 *   hacerlas y puede pintar con ellas. No cuestan radio, no cuestan batería, no
 *   le mienten a nadie y no tocan nada compartido.
 *
 *   TODO LO DEMÁS ESPERA ACTIVACIÓN:
 *     · red (cualquier petición al backend);
 *     · sondas del visor `SCR.*` (su contrato es "una pantalla recibió dato
 *       fresco" — una pantalla dormida no es una pantalla);
 *     · analíticas de vista (`*_viewed`: son escalones del embudo de admin y
 *       miden INTENCIÓN, no arranque de app);
 *     · `convergeWarehouse` con motivo de MONTAJE (su privilegio en PHOTO_WAKE
 *       está escrito sobre "esto es un acto del usuario, no un reloj");
 *     · lectura de la URL (el query string es del CHASIS, no de la pestaña:
 *       montadas, las cuatro raíces leerían el mismo `?session_id`);
 *     · escrituras al chasis compartido (el `scrollTop` del `<main>`,
 *       `history.replaceState`);
 *     · listeners de `document`;
 *     · polls y timers;
 *     · reacciones a foco / `visibilitychange` / NET.UP.
 *
 * EL DEFAULT SIN PROVEEDOR ES `true`, Y ES LA PIEZA QUE SOSTIENE TODO. Hoy el
 * router monta UNA sola raíz a la vez, así que montarse ES ser vista: `true` no
 * es un valor de relleno, es la verdad de hoy escrita una vez. De ahí salen
 * tres cosas a la vez:
 *   · PAGER-1 no estrena NI UN proveedor en producción. Cada raíz llama
 *     `useRaizActiva()` y recibe `true`; el comportamiento queda idéntico y lo
 *     único que cambia es que cada efecto ya declara de qué momento depende.
 *   · La web, las sub-navegaciones (`/study/exam`, `/study/flashcards`,
 *     `/study/adaptive`), Ajustes, Pendientes, el chasis entero (Sidebar, nav)
 *     y TODOS los arneses viejos siguen exactamente igual: para ellos "montado"
 *     sigue significando "activo", que en su caso es verdad — nadie los
 *     pre-monta.
 *   · Las pruebas del contrato envuelven la pantalla en un proveedor con
 *     `false` y la ven quedarse dormida, sin ningún gancho especial.
 *
 * DÓNDE VIVE EL PROVEEDOR MAÑANA. En PAGER-2 lo pone EL PAGER, por encima de
 * cada una de las cuatro raíces, con el booleano que sólo él conoce (cuál está
 * enfrente). No hay nada que retirar de las pantallas ese día: la misma línea
 * `useRaizActiva()` empieza a devolver `false` cuando toque.
 *
 * (De ahí que `RaizActivaProvider` hoy sólo aparezca en las pruebas: es la
 * mitad del contrato que todavía no tiene dueño en producción.)
 *
 * LA API ES SÓLO EL BOOLEANO. Nada de fases, ni de "casi activa", ni de
 * motivos: cada capa extra sería una regla nueva que cada efecto tendría que
 * interpretar. Un efecto o depende de la activación o no depende.
 */
export const RaizActivaContext = createContext(true);

/**
 * Envuelve una raíz y declara si está ACTIVA (el usuario la está viendo).
 * `activa` es obligatorio y se normaliza a booleano: quien envuelve tiene que
 * decidir, no dejarlo a medias.
 */
export function RaizActivaProvider({ activa, children }) {
  return (
    <RaizActivaContext.Provider value={Boolean(activa)}>
      {children}
    </RaizActivaContext.Provider>
  );
}

/** `true` = el usuario está viendo esta raíz. Sin proveedor arriba: `true`. */
export function useRaizActiva() {
  return useContext(RaizActivaContext);
}

export default useRaizActiva;
